import { redeemInviteCode } from "@/features/billing/api/billing";
import { ApiError } from "@/api/fetch";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { LogoLockup } from "@/components/ui/LogoLockup";
import { SkipToMainLink } from "@/components/layout/SkipToMainLink";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { AlertCircle, CheckCircle, Sparkles } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

type RedeemState = "idle" | "submitting" | "success" | "error";

export const RedeemPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const codeFromUrl = searchParams.get("code") ?? "";
  const [code, setCode] = useState(codeFromUrl);
  const [state, setState] = useState<RedeemState>("idle");
  const [error, setError] = useState("");
  const [planName, setPlanName] = useState("");
  const redeemedRef = useRef(false);

  useDocumentTitle("Redeem Invite Code");

  useEffect(() => {
    if (!codeFromUrl) return;
    setCode(codeFromUrl);
  }, [codeFromUrl]);

  useEffect(() => {
    if (loading || !user || !codeFromUrl || redeemedRef.current) return;
    redeemedRef.current = true;
    void handleRedeem(codeFromUrl);
  }, [loading, user, codeFromUrl]);

  const handleRedeem = async (codeToRedeem: string) => {
    if (!codeToRedeem.trim()) return;
    setState("submitting");
    setError("");
    try {
      const result = await redeemInviteCode(codeToRedeem);
      setPlanName(result.planName);
      setState("success");
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Failed to redeem code";
      setError(msg);
      setState("error");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleRedeem(code);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 text-sm text-zinc-500">
        Loading…
      </div>
    );
  }

  if (!user) {
    const next = `/redeem?code=${encodeURIComponent(codeFromUrl)}`;
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-gray-50 px-4">
        <SkipToMainLink />
        <LogoLockup className="mb-8" compactPaperclip={false} />
        <main
          id="main-content"
          className="w-full max-w-md bg-white rounded-xl border border-zinc-200 p-8"
        >
          <div className="text-center space-y-4">
            <Sparkles className="h-12 w-12 text-zinc-400 mx-auto" aria-hidden />
            <h1 className="text-2xl font-bold text-zinc-900">
              Redeem your invite code
            </h1>
            <p className="text-zinc-500 text-sm">
              Sign in or create an account to redeem your code and unlock free
              unlimited access.
            </p>
            <Button
              className="w-full"
              onClick={() =>
                navigate(`/login?next=${encodeURIComponent(next)}`)
              }
            >
              Continue to sign in
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (state === "success") {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-gray-50 px-4">
        <SkipToMainLink />
        <LogoLockup className="mb-8" compactPaperclip={false} />
        <main
          id="main-content"
          className="w-full max-w-md bg-white rounded-xl border border-zinc-200 p-8"
        >
          <div className="text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" aria-hidden />
            <h1 className="text-2xl font-bold text-zinc-900">
              You&apos;re all set!
            </h1>
            <p className="text-zinc-500">
              Your account is now on the{" "}
              <span className="font-semibold text-zinc-900">{planName}</span>{" "}
              plan. Enjoy unlimited access to Paperclip.
            </p>
            <Link to="/">
              <Button className="w-full">Start using Paperclip</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen items-center justify-center bg-gray-50 px-4">
      <SkipToMainLink />
      <LogoLockup className="mb-8" compactPaperclip={false} />
      <main
        id="main-content"
        className="w-full max-w-md bg-white rounded-xl border border-zinc-200 p-8"
      >
        <div className="text-center mb-6">
          <Sparkles className="h-12 w-12 text-zinc-400 mx-auto mb-4" aria-hidden />
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">
            Redeem invite code
          </h1>
          <p className="text-zinc-500 text-sm">
            Enter your invite code to unlock free unlimited access to Paperclip.
          </p>
        </div>

        {state === "error" && error && (
          <div
            role="alert"
            className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
          >
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="invite-code" className="block text-sm font-medium text-zinc-700 mb-1">
              Invite code
            </label>
            <input
              id="invite-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX-XXXX"
              className="w-full text-center text-xl tracking-widest font-mono px-4 py-3 h-auto rounded-lg border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
              autoFocus
              autoComplete="off"
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            loading={state === "submitting"}
            disabled={!code.trim() || state === "submitting"}
          >
            {state === "submitting" ? "Redeeming..." : "Redeem code"}
          </Button>
        </form>
      </main>
    </div>
  );
};